import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, apiError } from "../lib/api";
import { Building2, KeyRound, Eye, EyeOff } from "lucide-react";

function SectionCard({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="rounded-3xl bg-white border border-[#E8E5DE] overflow-hidden" data-testid={testid}>
      <div className="px-6 pt-6 pb-4 flex items-start gap-3.5 border-b border-[#F0EDE5]">
        <div className="h-10 w-10 rounded-xl bg-[#0B0D10] grid place-items-center shrink-0">
          <Icon size={17} className="text-[#C8FF4D]" strokeWidth={2} />
        </div>
        <div>
          <h3 className="font-display font-bold text-[16px] tracking-[-0.01em]">{title}</h3>
          {subtitle && <p className="text-[12.5px] text-[#3a3d44]/60 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function BusinessSection() {
  const { activeOrg, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", website: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (activeOrg) setForm({ name: activeOrg.name || "", phone: activeOrg.phone || "", website: activeOrg.website || "" });
  }, [activeOrg?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.name.trim()) { toast.error("Business name can't be empty."); return; }
    setSaving(true);
    try {
      await api.patch(`/orgs/${activeOrg.id}`, { name: form.name.trim(), phone: form.phone || null, website: form.website || null });
      await refresh();
      toast.success("Business details saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <SectionCard icon={Building2} title="Business" subtitle="How your business appears across BackPocket." testid="settings-business">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Label className="text-[12.5px]">Business name</Label><Input className="mt-1.5 h-11 bg-white rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" /></div>
        <div><Label className="text-[12.5px]">Phone</Label><Input className="mt-1.5 h-11 bg-white rounded-xl" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+61 412 345 678" data-testid="settings-phone" /></div>
        <div><Label className="text-[12.5px]">Website</Label><Input className="mt-1.5 h-11 bg-white rounded-xl" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="yourshop.com.au" data-testid="settings-website" /></div>
      </div>
      <div className="mt-4 flex items-center gap-4 text-[12.5px] text-[#3a3d44]/60">
        <span>Country <b className="text-[#0B0D10]">{activeOrg?.country}</b></span>
        <span>Currency <b className="text-[#0B0D10]">{activeOrg?.currency}</b></span>
        <span className="hidden sm:inline text-[#3a3d44]/40">Need these changed? Just ask us.</span>
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 bg-[#0B0D10] hover:bg-black text-white rounded-full h-10 px-5 text-[13px] font-semibold" data-testid="settings-save">
        {saving ? "Saving…" : "Save changes"}
      </Button>
    </SectionCard>
  );
}

function AccountSection() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e2) { toast.error(apiError(e2)); } finally { setSaving(false); }
  };

  return (
    <SectionCard icon={KeyRound} title="Account" subtitle="Your login and password." testid="settings-account">
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div><Label className="text-[12.5px]">Name</Label><div className="mt-1.5 h-11 rounded-xl bg-[#F4F1E9] border border-[#E8E5DE] px-3.5 flex items-center text-[14px] text-[#0B0D10]" data-testid="settings-user-name">{user?.name}</div></div>
        <div><Label className="text-[12.5px]">Email</Label><div className="mt-1.5 h-11 rounded-xl bg-[#F4F1E9] border border-[#E8E5DE] px-3.5 flex items-center text-[14px] text-[#0B0D10] truncate" data-testid="settings-user-email">{user?.email}</div></div>
      </div>
      <form onSubmit={changePassword}>
        <div className="text-[12px] uppercase tracking-[0.14em] text-[#3a3d44]/55 font-medium mb-3">Change password</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="relative">
            <Label className="text-[12.5px]">Current</Label>
            <Input type={show ? "text" : "password"} required className="mt-1.5 h-11 bg-white rounded-xl pr-10" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="settings-pw-current" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 bottom-3 text-[#3a3d44]/40 hover:text-[#0B0D10]">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>
          </div>
          <div><Label className="text-[12.5px]">New password</Label><Input type={show ? "text" : "password"} required minLength={8} className="mt-1.5 h-11 bg-white rounded-xl" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="settings-pw-new" /></div>
          <div><Label className="text-[12.5px]">Confirm new</Label><Input type={show ? "text" : "password"} required minLength={8} className="mt-1.5 h-11 bg-white rounded-xl" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="settings-pw-confirm" /></div>
        </div>
        <Button type="submit" disabled={saving || !pw.current || !pw.next} className="mt-5 bg-[#0B0D10] hover:bg-black text-white rounded-full h-10 px-5 text-[13px] font-semibold" data-testid="settings-pw-submit">
          {saving ? "Updating…" : "Update password"}
        </Button>
      </form>
    </SectionCard>
  );
}

export default function Settings() {
  const { activeOrg } = useAuth();
  if (!activeOrg) return null;
  return (
    <div className="max-w-3xl">
      <PageHeader title="Settings" subtitle="Your business details and account." />
      <div className="space-y-6">
        <BusinessSection />
        <AccountSection />
      </div>
    </div>
  );
}
